/**
 * Utility function to concat classNames.
 *
 * Usage: classnames('css_class1', 'css_class1')
 *
 * Can be used with objects where the keys are css classes and the
 * values are booleans that decide if classes are active or not:
 *
 * Example: classnames('input', { 'input-error': has_errors })
 *
 * @param  {...any} args
 * @returns string
 */
import paths from 'constants/paths';

type ClassnameObject = {
  [key: string]: string | boolean | number,
};

type Classname = ClassnameObject | string;

function classnames(...args: Classname[]): string {
  if (args.length === 1) {
    const [firstEntry] = args;
    if (firstEntry && typeof firstEntry === 'object') {
      /* firstEntry's keys whose value is truthy */
      const activeClasses = Object.entries(firstEntry)
        .filter(([, value]) => value).map(([key]) => key);
      return activeClasses.join(' ');
    }
    return firstEntry;
  }
  return args.filter((entry) => !!entry).map((value) => classnames(value)).join(' ');
}

function kebabToCamel(kebab: string): string {
  return kebab.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
}

const isValidSlug = (link: string): boolean => {
  const pattern = /^(\/|https?:\/\/(www\.)?teethtalkgirl\.com|www\.(teethtalkgirl\.com))(?!.*\/\/|\.)|^((?!(https?:\/\/|www\.)).)*$/;
  return pattern.test(link);
};

const isWindowDefined = typeof window !== 'undefined';

const getFullName = (prefix: string, name: string, lastName: string, suffix: string) => `${prefix ? `${prefix} ` : ''}${name} ${lastName}${suffix ? `, ${suffix}` : ''}`;

const createArticleUrl = (categorySlug: string, subcategorySlug: string, slug: string): string => {
  if (categorySlug !== 'career') return `/${categorySlug}/${slug}`;
  return `/${categorySlug}/${subcategorySlug}/${slug}`;
};

const createReviewUrl = (slug: string): string => `${paths.reviews}/${slug}`;

const createAuthorsUrl = (slug: string): string => `${paths.authors}/${slug}`;

const createReviewersUrl = (slug: string): string => `${paths.reviewers}/${slug}`;

const createTopicsUrl = (slug: string): string => `${paths.topics}/${slug}`;

/* If the url is youtu.be or the full url we need to split it, if not the url already contains
 embed, and it doesn't need to modify it */
const createYoutubeUrl = (url: string): string => {
  if (url.includes('youtu.be')) {
    const id = url.split('youtu.be/')[1];
    return `https://youtube.com/embed/${id}`;
  }
  if (url.includes('watch?v=')) {
    const id = url.split('watch?v=')[1];
    return `https://youtube.com/embed/${id}`;
  }
  return url;
};

const getYoutubeId = (url: string): string => {
  if (url.includes('youtu.be')) {
    return url.split('youtu.be/')[1];
  }
  if (url.includes('watch?v=')) {
    return url.split('watch?v=')[1];
  }
  return url.split('/embed/')[1];
};

/* Returns a placeholder video that triggers an onClick on mouseover, this is to prevent the double
* click on it */
const getVideoSrcDoc = (url: string, id: string): string => `<style>
            body, .full {
                width: 100%;
                height: 100%;
                margin: 0;
                position: absolute;
                display: flex;
                justify-content: center;
                object-fit: cover;
            }
        </style>
        <a
            href='${url}'
            class='full'
        >
            <img
                src='https://img.youtube.com/vi/${id}/hqdefault.jpg'
                class='full'
                onmouseover='this.click()'
            />
            <svg
                version='1.1'
                viewBox='0 0 68 48'
                width='68px'
                style='position: relative;'
            >
                <path d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z' fill='#f00'></path>
                <path d='M 45,24 27,14 27,34' fill='#fff'></path>
            </svg>
        </a>`;

export {
  classnames, kebabToCamel, isValidSlug, isWindowDefined, getFullName, createArticleUrl,
  createAuthorsUrl, createReviewersUrl, createReviewUrl, createTopicsUrl, createYoutubeUrl,
  getYoutubeId, getVideoSrcDoc,
};
